// The reviver's third argument, `context.source`, is the raw JSON text
// for the current value. Older Firefox/Safari don't ship it, so the
// polyfill is required to recover full-precision digits below.
import "core-js/actual/json/parse";

/**
 * `JSON.parse` reviver that coerces nanosecond-epoch fields to `bigint`
 * instead of the lossy `Number` `JSON.parse` would produce.
 *
 * `Number.MAX_SAFE_INTEGER` corresponds to April 15, 1970 in ns since
 * epoch, so every modern ns timestamp is silently truncated by the
 * default parse. We recover the exact digits by re-parsing the raw
 * token text (`context.source`) with `BigInt`.
 *
 * The reviver sees every key in every payload, so the allowlist below
 * is load-bearing — adding a key coerces that field app-wide.
 *
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/JSON/parse#the_reviver_parameter
 * https://github.com/tc39/proposal-json-parse-with-source
 */
export function parseTimestampsAsBigInt(
  key: string,
  value: unknown,
  context?: { source: string },
) {
  // Coerced keys (and where they come from):
  //   - `start_time`, `end_time`: Topic and Event ns-epoch bounds.
  //   - `t`: ns-epoch instant inside a magic-link context object
  //     (`shared/components/RobotoMarkdown/magicLinks.ts`).
  //   - `min_timestamp_ns`, `max_timestamp_ns`: Session aggregate bounds.
  // `context` may be missing on engines without the source-text arg
  // when the polyfill fails to install; `null` literals are valid for
  // the nullable bounds. Both fall through to `value`.
  if (
    (key === "start_time" ||
      key === "end_time" ||
      key === "t" ||
      key === "min_timestamp_ns" ||
      key === "max_timestamp_ns") &&
    context !== undefined &&
    value !== null
  ) {
    try {
      return BigInt(context.source);
    } catch {
      // `BigInt(...)` throws when the token isn't an integer — most
      // often a customer's user-metadata reusing one of these keys with
      // a non-integer value. Fall back to the lossy Number so the field
      // round-trips; first-class Roboto fields are always integers and
      // never reach this branch.
      return value;
    }
  }

  return value;
}
